import { useState, useEffect, useMemo, useRef } from 'react';
import { ClassNames, isEmpty } from '@/utils';
import MobxWrap from '@/components/MobxWrap';
import KlarnaMessage from '@/components/KlarnaMessage';
import { enableKlarna } from '@/utils/configs/payment.config';
import { EcommerceTracker, GlobalTracker } from '@/utils/Tracker';
import styles from './index.module.less';
import { cloneDeep } from 'lodash';

const TopBar = MobxWrap(['area', 'meta', 'app', 'abtest'], (props) => {
  const { showMobileNav } = props;
  const { country, toggleAreaModal } = props.area;
  const { navCurStyle } = props.meta;
  const { G_IsHomePage, isCartPage } = props.app;

  const onClickLink = () => {
    GlobalTracker.navigation({ action: 'official_website' });
  };

  return (
    <div
      id='header_topbar'
      style={{ backgroundColor: navCurStyle?.topbarBgColor ?? '#f8f9fc' }}
      className={ClassNames([styles.topBar, showMobileNav ? styles.mobileStyle : ''])}
      data-ishomepage={G_IsHomePage || isCartPage}
    >
      <div id='topBarContainer' className={styles.container}>
        <a
          className={styles.toInsta360}
          href='https://www.insta360.com'
          target='_blank'
          rel='noopener noreferrer'
          onClick={onClickLink}
        >
          insta360.com
        </a>
        <TopBarList meta={props.meta} app={props.app} area={props.area} abtest={props.abtest} />
        <button className={styles.selectArea} onClick={toggleAreaModal}>
          <span className={styles.icon} />
          {country?.name}
          {` ( ${country?.language_text} / ${country?.signal} ${country?.currency} )`}
        </button>
      </div>
    </div>
  );
});

const TopBarList = MobxWrap(['area', 'meta', 'abtest'], ({ area, meta, abtest }) => {
  const {
    country: { code },
  } = area;
  const { isInVWOabtNotShowPrime } = abtest;
  const { topBarInfos: _topBarInfos, freeShippingAmount } = meta;
  // 在轮播方法里的数据使用ref数据
  const topBarInfosRef = useRef([]);
  const [isPaused, setPaused] = useState(false); // 标记是否处于暂停状态
  let timer = null;
  let startTime = null;
  let isTransitioning = false; // 标记是否正在过渡
  let index = 0;
  const defaultDelay = 2500;
  const TopBarListRef = useRef(null);
  const TopbarWrapRef = useRef(null);
  const topbarHeight = 40;

  const otherInfos = useMemo(() => {
    const res = [];
    if (enableKlarna({ code })) {
      res.push({
        id: 'klarna',
        topBarName: 'klarna',
        entryDom: (
          <KlarnaMessage id='topbar' dataKey='top-strip-promotion-auto-size' className={styles.KlarnaMessage} />
        ),
      });
    }

    if (!isInVWOabtNotShowPrime && code === 'US') {
      res.push({
        id: 'prime',
        topBarName: 'prime',
        entryInfo: `<p><strong>Buy with Prime</strong> | Get FREE 1–2 day shipping and hassle-free returns</p>`,
      });
    }
    return res;
  }, [isInVWOabtNotShowPrime]);

  // 动态变更赋值topBarInfos的数据
  const topBarInfos = useMemo(() => {
    let MetaTopBarInfos = cloneDeep(_topBarInfos);

    const now = +new Date();
    const needReplaceTopbarId = [7];
    MetaTopBarInfos = MetaTopBarInfos.filter((item) => {
      return item?.startTime < now && item?.endTime > now;
    });

    if (MetaTopBarInfos.length === 0) {
      return otherInfos;
    } else if (MetaTopBarInfos.length === 1) {
      MetaTopBarInfos.push(...otherInfos);
      MetaTopBarInfos.push(MetaTopBarInfos[0]);
    } else {
      MetaTopBarInfos.splice(MetaTopBarInfos.length - 1, 0, ...otherInfos);
    }

    // 将运费文案中的${}替换为包邮门槛金额
    MetaTopBarInfos.forEach((item) => {
      if (needReplaceTopbarId.includes(item.topBarId)) {
        item.entryInfo = item.entryInfo.replace(/\$\{[^}]*\}/g, freeShippingAmount.shippingCost.formatPrice);
      }
    });

    // 对topbar中的超链接加上埋点
    MetaTopBarInfos.forEach((item) => {
      const res = item.entryInfo;
      // 匹配所有的超链接
      const links = item.entryInfo?.match(/<a[^>]*href="([^"]*)(?=")/g);
      links?.forEach((link) => {
        // 根据超链接中的参数调整连接符
        let bound = link.includes('?') ? '&' : '?';
        item.entryInfo = res.replace(link, `${link}${bound}from=topbar`);
      });
    });

    topBarInfosRef.current = MetaTopBarInfos;
    return MetaTopBarInfos;
  }, [_topBarInfos, otherInfos, freeShippingAmount.shippingCost.formatPrice]);

  useEffect(() => {
    const freeShippingTipID = 7;
    const freeShippingTip = topBarInfosRef.current.filter((o) => o.topBarId === freeShippingTipID)[0];
    meta.setStateValue('freeShippingTip', freeShippingTip?.entryInfo || '');
  }, []);

  const handleAutoChange = () => {
    if (timer) {
      cancelAnimationFrame(timer);
      timer = null; // 需要手动致空变量
    }

    if (topBarInfosRef.current?.length === 1 || isEmpty(topBarInfosRef.current) || isPaused) return; // 只有一条或鼠标悬浮的时候不轮播

    startTime = performance.now?.();
    timer = requestAnimationFrame(handleChangeNext);
  };

  const onTransitionEnd = () => {
    // 过渡结束后，更新状态并启动下一次轮播
    isTransitioning = false;
    handleAutoChange();
  };

  // 轮播
  const handleChangeNext = () => {
    if (TopBarListRef.current && !isTransitioning) {
      const currentTime = performance.now?.();
      const elapsedTime = currentTime - startTime;

      if (elapsedTime >= defaultDelay) {
        const isLast = index === topBarInfosRef.current.length - 1;
        const nextIndex = isLast ? 0 : index + 1;

        // 如果是最后一个元素，取消过渡效果
        if (isLast) {
          TopBarListRef.current.style.transition = 'none';
          // 立即跳转到第一个元素
          requestAnimationFrame(() => {
            index = nextIndex;
            TopBarListRef.current.style.transform = `translate3d(0,${topbarHeight * index * -1}px,0)`;
            isTransitioning = false;
            handleAutoChange();
          });
        } else {
          TopBarListRef.current.style.transition = 'all .3s ease-in-out';
          index = nextIndex;
          TopBarListRef.current.style.transform = `translate3d(0,${topbarHeight * index * -1}px,0)`;

          // 标记正在过渡
          isTransitioning = true;

          const topBarVisible = !document.getElementById('g-header')?.className.includes('g-header-hide');
          const item = topBarInfosRef.current[index];

          if (topBarVisible && !isLast && item && !item.alreadySend) {
            // topbar可见
            item.alreadySend = true;
            EcommerceTracker.viewPromotion({ item: { item_id: item.topBarId, item_name: item.topBarName } });
          }

          // 监听过渡结束事件
          TopBarListRef.current.addEventListener('transitionend', onTransitionEnd, { once: true });
        }
      }

      // 继续请求下一帧动画，以实现连续的轮播
      timer = requestAnimationFrame(handleChangeNext);
    }
  };

  // 停止和启动
  const handleToggleAutoChange = (state) => {
    if (state === 'stop') {
      setPaused(true);
      cancelAnimationFrame(timer);
    } else {
      setPaused(false);
      handleAutoChange();
    }
  };

  const handleItemClick = (item) => {
    EcommerceTracker.selectPromotion({ item: { item_id: item.topBarId, item_name: item.topBarName } });
  };

  useEffect(() => {
    // 添加悬浮事件
    const ele = TopbarWrapRef.current;
    if (!ele) return;
    ele.addEventListener('mouseenter', () => {
      handleToggleAutoChange('stop');
    });
    ele.addEventListener('mouseleave', () => {
      handleToggleAutoChange('start');
    });

    return () => {
      if (!ele) return;
      ele.removeEventListener('mouseenter', () => {
        handleToggleAutoChange('stop');
      });
      ele.removeEventListener('mouseleave', () => {
        handleToggleAutoChange('start');
      });
    };
  }, []);

  useEffect(() => {
    handleAutoChange();
  }, []);

  if (!topBarInfos[0]) return null;

  return (
    <div className={styles.TopBarList} ref={TopbarWrapRef}>
      <div className={styles.wrap} id='TopBarList' ref={TopBarListRef}>
        {topBarInfos.map((o, k) => (
          <div className={styles.item} key={k} onClick={() => handleItemClick(o)}>
            {o.entryDom ? (
              o.entryDom
            ) : (
              <div className={styles.value} dangerouslySetInnerHTML={{ __html: o.entryInfo }} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
});

export default TopBar;
